import React, { FC, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { Button, FormControl, InputLabel, Menu, MenuItem, Popover, Stack } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useTranslation } from "react-i18next";
import { fiscalPeriodsCount } from "../../common/fiscalPeriods";
import { useCurrentLanguage } from "../../i18n";
import { UserPeriodFilter } from "../../state/company";
// Required for localization.
import "dayjs/locale/es";
import "dayjs/locale/it";
import "dayjs/locale/en";

dayjs.extend(localizedFormat);

interface Props {
	filter: UserPeriodFilter | undefined;
	setFilter: (filter: UserPeriodFilter) => void;
	variant: "complex" | "simple";
	readOnly?: boolean;
}

const PeriodSelector: FC<Props> = ({ filter, setFilter, variant, readOnly }) => {
	const today = dayjs();
	const { t } = useTranslation();

	const language = useCurrentLanguage();

	const [popupAnchor, setPopupAnchor] = useState<HTMLElement | null>(null);
	const [fiscalYearIsOpen, setFiscalYearIsOpen] = useState<boolean>(false);
	const [customPeriodIsOpen, setCustomPeriodIsOpen] = useState<boolean>(false);

	const initialEnd = filter?.end && filter.end.isBefore(today, "day") ? filter.end : today;
	const initialStart = filter?.start && filter.start.isBefore(today, "day") ? filter.start : today.subtract(1, 'year');

	const [customStart, setCustomStart] = useState<Dayjs | null>(initialStart);
	const [customEnd, setCustomEnd] = useState<Dayjs | null>(initialEnd);

	const [menuWidth, setMenuWidth] = useState<number | null>(null);

	const label =
		filter?.type === "custom"
			? t("custom period")
			: filter
				? t("fiscal year {{year}}", { year: filter.start.year() })
				: "";

	const renderPeriod = (start: Dayjs, end: Dayjs): string =>
		`${start.locale(language).format("L")} - ${end.locale(language).format("L")}`;

	const openFiscalYearSelector = (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
		setPopupAnchor(event.currentTarget);
		setCustomPeriodIsOpen(false);
		setFiscalYearIsOpen(true);
		setMenuWidth(event.currentTarget.offsetWidth);
	};

	const closeMenu = () => {
		setCustomPeriodIsOpen(false);
		setFiscalYearIsOpen(false);
		setPopupAnchor(null);
	};

	const openCustomPeriod = () => {
		setFiscalYearIsOpen(false);
		setCustomPeriodIsOpen(true);
	};

	const confirmCustomPeriod = () => {
		closeMenu();
		setFilter({
			type: "custom",
			end: customEnd!,
			start: customStart!,
			fiscalYears: filter?.fiscalYears ?? [],
			selectedPeriodId: fiscalPeriodsCount + 1,
		});
	};

	if (!filter) {
		// User period filter is not yet available.
		// This filter will be available at the next render cycle.
		return <></>;
	}

	return (
		<>
			<FormControl>
				{variant !== "simple" && (
					<InputLabel shrink className="fiscal-period-label">
						{label}
					</InputLabel>
				)}

				<Button
					disabled={readOnly}
					startIcon={<DateRangeIcon sx={{ marginLeft: 1 }} />}
					endIcon={
						<ChevronRightIcon
							style={{
								transition: "transform .125s",
								transform: `rotate(${fiscalYearIsOpen ? "90deg" : "0deg"})`,
							}}
						/>
					}
					className="fiscal-period-selector"
					onClick={(e) => openFiscalYearSelector(e)}
					color={variant === "complex" ? "primary" : "inherit"}
					sx={{
						fontWeight: variant === "complex" ? 500 : 400,
					}}
				>
					{renderPeriod(filter.start, filter.end)}
				</Button>
			</FormControl>
			<Menu
				open={fiscalYearIsOpen}
				anchorEl={popupAnchor}
				onClose={closeMenu}
				slotProps={{
					paper: {
						sx: {
							width: menuWidth ?? "auto", // Dynamically match anchor's width
						},
					},
				}}
			>
				{filter.fiscalYears.map((year) => (
					<MenuItem
						key={year.id}
						value={year.id}
						sx={{ width: "100%" }}
						onClick={() => {
							closeMenu();
							setFilter({
								...filter,
								end: year.end,
								type: "fiscal",
								start: year.start,
								selectedPeriodId: year.id,
							});
						}}
						selected={year.id === filter.selectedPeriodId}
					>
						{t("fiscal year {{year}}", { year: year.start.year() })}
					</MenuItem>
				))}
				<MenuItem key={"custom"} value={"custom"} onClick={openCustomPeriod} sx={{ width: "100%" }}>
					{t("custom period")}
				</MenuItem>
			</Menu>
			<Popover open={customPeriodIsOpen} anchorEl={popupAnchor} onClose={closeMenu}>
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
					<Stack direction="column" spacing={1} sx={{ p: 2 }}>
						<Stack direction="row" spacing={1}>
							<DateCalendar
								value={customStart}
								onChange={(newValue) => {
									if (!newValue) return;

									setCustomStart(newValue);

									// Keep the end date valid when the start date moves forward
									if (!customEnd || customEnd.isBefore(newValue, "day")) {
										setCustomEnd(newValue);
									}
								}}
							/>

							<DateCalendar
								minDate={customStart ?? undefined}
								value={customEnd}
								onChange={(newValue) => {
									if (!newValue) return;

									setCustomEnd(newValue);
								}}
							/>
						</Stack>
						<Stack direction="row-reverse" spacing={2}>
							<Button className="confirm" onClick={confirmCustomPeriod}>
								{t("apply")}
							</Button>
							<Button className="cancel" onClick={closeMenu}>
								{t("cancel")}
							</Button>
						</Stack>
					</Stack>
				</LocalizationProvider>
			</Popover>
		</>
	);
};

export default PeriodSelector;
